<template>
  <section id="work-experience-section">
    <h2 class="title text-center section-title">Work Experience</h2>
    <h3 class="text-center">Chronological Order</h3>

    <v-row class="d-flex justify-center">
      <v-spacer class="hidden-md-and-down"></v-spacer>
      <v-col :cols="getColsForTimeline" class="d-flex justify-center">
        <v-timeline align-top :dense="$vuetify.breakpoint.smAndDown">
          <v-timeline-item
            v-for="(item, i) in workExperience"
            :key="i"
            :color="item.color"
            :icon="item.icon"
            fill-dot
            class="work-timeline"
          >
            <v-card :color="item.color" dark>
              <v-card-title class="title">{{ item.company }}</v-card-title>
              <v-card-subtitle class="subtitle">
                <span class="job-position-text">{{ item.position }}</span
                ><br />
                {{ getDate(item.startDate, item.endDate) }}
              </v-card-subtitle>
              <v-card-text class="white text--primary bullet-points">
                <p v-for="(point, idx) in item.descriptionPoints" :key="idx">
                  - {{ point }}
                </p>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-col>
      <v-spacer class="hidden-md-and-down"></v-spacer>
    </v-row>
  </section>
</template>

<script>
import moment from "moment";

export default {
  data: () => ({
    workExperience: [
      /* ---------- Delivery Hero ---------- */
      {
        company: "Delivery Hero SE",
        position: "Senior Software Engineer",
        startDate: "01/02/2024",
        endDate: null,
        color: "indigo darken-3",
        icon: "mdi-briefcase",
        descriptionPoints: [
          "Promoted to Senior Software Engineer.",
          "Leading features on the recommendation-distribution engine for restaurants and vendors globally.",
          "Designing and maintaining **highly-scalable, mission-critical systems serving millions of users globally** with near-zero downtime.",
          "Driving architectural improvements, code-quality initiatives and mentoring new engineers.",
        ],
      },
      {
        company: "Delivery Hero SE",
        position: "Software Engineer",
        startDate: "01/12/2022",
        endDate: "31/01/2024",
        color: "blue darken-3",
        icon: "mdi-briefcase",
        descriptionPoints: [
          "Worked on production-level engine that handles recommendation distribution to restaurants and vendors.",
          "Migrated critical micro-services from GoLang to Java Spring.",
          "Developed PUSH/PULL APIs handling hundreds of thousands of calls with zero downtime.",
          "Tech: Java Spring | GoLang | AWS | Kubernetes | Kafka | PostgreSQL.",
        ],
      },

      /* ---------- LOVOO ---------- */
      {
        company: "LOVOO GmbH",
        position: "Software Engineer (Full Stack)",
        startDate: "01/12/2021",
        endDate: "30/11/2022",
        color: "cyan darken-2",
        icon: "mdi-briefcase",
        descriptionPoints: [
          "Built an internal project to detect and prevent ad-fraud, bots, and spam.",
          "Integrated algorithms for detecting ad farms using meta-data parsing, geolocation & behaviour patterns.",
          "Worked directly with stakeholders for requirements and redesigned the web UI.",
          "Tech: GoLang | Vue JS | GCP | Kubernetes | Kafka.",
        ],
      },

      /* ---------- Cardinal Health ---------- */
      {
        company: "Cardinal Health (Fortune 500)",
        position: "Senior Software Engineer",
        startDate: "01/08/2021",
        endDate: "30/11/2021",
        color: "deep-purple darken-2",
        icon: "mdi-briefcase",
        descriptionPoints: [
          "Promoted to Senior Software Engineer after high-impact delivery.",
        ],
      },
      {
        company: "Cardinal Health (Fortune 500)",
        position: "Software Engineer",
        startDate: "01/06/2019",
        endDate: "31/07/2021",
        color: "deep-purple lighten-1",
        icon: "mdi-briefcase",
        descriptionPoints: [
          "Delivered 45+ features & 150+ bug-fixes (handled >40% of sprint story-points).",
          "Resolved 37+ field issues remotely saving thousands in travel costs.",
          "Built automation for 60%+ of new features.",
          "Developed 2 Java libraries and field-support scripts for NetOps.",
          "Fixed memory leaks in both frontend (Vue JS) and backend (Java).",
          "Stack: Java (backend) | Vue JS | PostgreSQL | Linux | Git.",
        ],
      },

      /* ---------- AUB ---------- */
      {
        company: "American University of Beirut",
        position: "Web Developer (Part-Time)",
        startDate: "01/07/2018",
        endDate: "30/06/2019",
        color: "green darken-2",
        icon: "mdi-school",
        descriptionPoints: [
          "Developed and maintained the ABLE Summit 2019 website (WordPress).",
          "Implemented WCAG 2.0 accessibility compliance.",
          "Awarded by AUB CIO for outstanding effort and ownership.",
          "Migrated AUB IT’s website to new SharePoint platform.",
        ],
      },
    ],
  }),

  computed: {
    getColsForTimeline() {
      switch (this.$vuetify.breakpoint.name) {
        case "lg":
          return 8;
        case "xl":
          return 8;
        default:
          return 11;
      }
    },
  },

  methods: {
    getDate(startDate, endDate) {
      const start = moment(startDate, "DD/MM/YYYY");
      const startStr = start.format("MMM-YYYY");

      if (endDate) {
        const end = moment(endDate, "DD/MM/YYYY");
        const endStr = end.format("MMM-YYYY");
        const dur = end.diff(start);
        const y = moment.duration(dur).years();
        const m = moment.duration(dur).months();
        const diff = y > 0 ? `( ${y}Y ${m}M )` : `( ${m}M )`;
        return `${startStr} – ${endStr} ${diff}`;
      }

      const today = moment();
      const dur = today.diff(start);
      const y = moment.duration(dur).years();
      const m = moment.duration(dur).months();
      const diff = y > 0 ? `( ${y}Y ${m}M )` : `( ${m}M )`;
      return `${startStr} – Present ${diff}`;
    },
  },
};
</script>

<style scoped>
.section-title {
  margin-top: 25px;
}
.bullet-points {
  padding-top: 25px;
}
.job-position-text {
  color: white;
  font-size: 1.25em;
}
</style>
